import { PageError, Panel } from '@cmg/common';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import systemManagementApiClient from '../../../common/api/systemManagementApiClient';
import LoadingIndicator from '../../../common/components/indicators/loading-indicator/LoadingIndicator';
import { useDocumentTitle } from '../../../common/hooks/useDocumentTitle/useDocumentTitle';
import routeFactory from '../../../common/util/routeFactory';
import type { Trait } from '../../../types/domain/trait/Trait';
import PermissionList from './components/permission-list/PermissionList';
import TraitAccountList from './components/trait-account-list/TraitAccountList';

export type RouteParams = {
  traitCode: string;
};

/**
 * Page to view a specific Trait's details (Trait metadata, trait permissions, accounts that have the trait).
 *
 * Potential future enhancement - ability to assign and revoke traits on accounts directly from this page.
 */
const TraitDetailRoute: React.FC = () => {
  const { traitCode } = useParams<RouteParams>();
  const [trait, setTrait] = useState<Trait | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<React.ComponentProps<typeof PageError>['error'] | null>(null);

  useDocumentTitle(
    routeFactory.adminTraitDetail.getDocumentTitle({
      traitName: trait?.name,
    })
  );

  useEffect(() => {
    if (!traitCode) return;
    setLoading(true);
    void systemManagementApiClient.admin.getTrait(traitCode).then(response => {
      if (response.ok) {
        setTrait(response.data);
      } else {
        setError(response.data.error);
      }
      setLoading(false);
    });
  }, [traitCode]);

  if (loading) {
    return (
      <Panel>
        <LoadingIndicator>Loading trait...</LoadingIndicator>
      </Panel>
    );
  }

  if (error) {
    return <PageError error={error} />;
  }

  return (
    <Panel fillViewport>
      <Panel.Header
        title={`Trait - ${trait?.name || ''}`}
        bottomContent={
          <div>
            <div>{trait?.description || ''}</div>
            <PermissionList permissions={trait?.permissions} />
          </div>
        }
      />
      {traitCode && <TraitAccountList traitCode={traitCode} />}
    </Panel>
  );
};

export default TraitDetailRoute;
